/**
 * EmailInsightsTab Component
 *
 * Displays AI-extracted email insights for a contact.
 * Shows email activity, topics, related entities, etc.
 */

import { useTranslation } from 'react-i18next';
import type { ContactEmailInsights } from '../types';

interface EmailInsightsTabProps {
  insights: ContactEmailInsights | null;
  loading: boolean;
  onRefresh: () => void;
  onApproveMerge?: () => void;
  onRejectMerge?: () => void;
}

export default function EmailInsightsTab({
  insights,
  loading,
  onRefresh,
  onApproveMerge,
  onRejectMerge,
}: EmailInsightsTabProps) {
  const { t } = useTranslation('people');
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="text-center py-12 text-gray-500">
        <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <p className="font-medium">{t('email_insights_tab.empty_state.title')}</p>
        <p className="text-sm mt-1">{t('email_insights_tab.empty_state.description')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Review Status Banner (if pending) */}
      {insights.review_status === 'pending' && insights.candidate_match && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <h4 className="font-medium text-amber-800">{t('email_insights_tab.pending_review.title')}</h4>
              <p className="text-sm text-amber-700 mt-1">
                {t('email_insights_tab.pending_review.merge_suggestion', { name: insights.candidate_match.name })}
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Confidence: {Math.round((insights.candidate_match.confidence || 0) * 100)}% - {insights.candidate_match.reasoning}
              </p>
              {onApproveMerge && onRejectMerge && (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={onApproveMerge}
                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  >
                    {t('email_insights_tab.pending_review.approve_merge')}
                  </button>
                  <button
                    onClick={onRejectMerge}
                    className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                  >
                    {t('email_insights_tab.pending_review.keep_separate')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Extraction Summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="font-medium text-gray-900 flex items-center gap-2">
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          {t('email_insights_tab.ai_extraction_summary')}
        </h4>
        <div className="mt-3 grid gap-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">{t('email_insights_tab.labels.source')}</span>
            <span className="text-gray-900">{t('email_insights_tab.extracted_from', { count: insights.total_emails })}</span>
          </div>
          {insights.ai_confidence && (
            <div className="flex justify-between">
              <span className="text-gray-500">{t('email_insights_tab.labels.ai_confidence')}</span>
              <span className="text-gray-900">{Math.round(insights.ai_confidence * 100)}%</span>
            </div>
          )}
          {insights.last_updated_at && (
            <div className="flex justify-between">
              <span className="text-gray-500">{t('email_insights_tab.labels.last_updated')}</span>
              <span className="text-gray-900">{new Date(insights.last_updated_at).toLocaleString()}</span>
            </div>
          )}
        </div>
        <button
          onClick={onRefresh}
          className="mt-3 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {t('email_insights_tab.refresh_insights')}
        </button>
      </div>

      {/* Email Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="font-medium text-gray-900 flex items-center gap-2">
          <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          {t('email_insights_tab.email_activity')}
        </h4>
        <div className="mt-3 grid gap-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">{t('email_insights_tab.labels.total_emails')}</span>
            <span className="text-gray-900">
              {insights.received_from_contact ?? insights.emails_sent} received, {insights.sent_to_contact ?? insights.emails_received} sent
            </span>
          </div>
          {insights.first_contact_at && (
            <div className="flex justify-between">
              <span className="text-gray-500">{t('email_insights_tab.labels.first_contact')}</span>
              <span className="text-gray-900">{new Date(insights.first_contact_at).toLocaleDateString()}</span>
            </div>
          )}
          {insights.last_contact_at && (
            <div className="flex justify-between">
              <span className="text-gray-500">{t('email_insights_tab.labels.last_contact')}</span>
              <span className="text-gray-900">{new Date(insights.last_contact_at).toLocaleDateString()}</span>
            </div>
          )}
          {insights.avg_response_time_hours && (
            <div className="flex justify-between">
              <span className="text-gray-500">{t('email_insights_tab.labels.avg_response_time')}</span>
              <span className="text-gray-900">{insights.avg_response_time_hours.toFixed(1)} hours</span>
            </div>
          )}
        </div>
      </div>

      {/* Location */}
      {insights.location && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t('email_insights_tab.location')}
          </h4>
          <p className="mt-2 text-gray-700">{insights.location}</p>
        </div>
      )}

      {/* Topics */}
      {insights.topics && insights.topics.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            {t('email_insights_tab.topics_discussed')}
          </h4>
          <div className="mt-3 flex flex-wrap gap-2">
            {insights.topics.map((topic, i) => (
              <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded">
                {topic}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Related Entities */}
      {(insights.related_people?.length > 0 || insights.related_companies?.length > 0) && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            {t('email_insights_tab.related_entities')}
          </h4>
          <div className="mt-3 space-y-3">
            {insights.related_people && insights.related_people.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 uppercase mb-2">{t('email_insights_tab.people')}</p>
                <div className="space-y-1">
                  {insights.related_people.slice(0, 5).map((person) => (
                    <div key={person.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-900">{person.name}</span>
                      <span className="text-gray-500">{person.email_count} emails</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {insights.related_companies && insights.related_companies.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 uppercase mb-2">{t('email_insights_tab.companies')}</p>
                <div className="space-y-1">
                  {insights.related_companies.slice(0, 5).map((company) => (
                    <div key={company.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-900">{company.name}</span>
                      <span className="text-gray-500">{company.contact_count} contacts</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
